// src/Components/SeoHead.jsx
// Per-route title/description/canonical/OG. index.html ships one static
// block (tagged with stable ids below) as the fallback every route shows
// until React mounts — and what a crawler or share-card bot that never
// runs JS at all sees, on any route. This component OVERWRITES those exact
// elements in place once a route mounts, rather than injecting new ones
// alongside them: a library like react-helmet doesn't know about tags it
// didn't create, so it only ever adds a second, competing title/meta next
// to the static one instead of replacing it — exactly the bug this
// sidesteps by targeting the static tags directly, by id.
import { useEffect } from "react";

const SITE_URL = "https://name-it-ashy.vercel.app";

function setMetaContent(id, content) {
  const el = document.getElementById(id);
  if (el) el.setAttribute("content", content);
}

export default function SeoHead({ title, description, path, noindex = false }) {
  useEffect(() => {
    const canonicalHref = `${SITE_URL}${path}`;

    const titleEl = document.getElementById("seo-title");
    if (titleEl) titleEl.textContent = title;

    setMetaContent("seo-description", description);
    setMetaContent("seo-og-title", title);
    setMetaContent("seo-og-description", description);
    setMetaContent("seo-twitter-title", title);
    setMetaContent("seo-twitter-description", description);

    // og:url has no static tag in index.html (it's meaningless without a
    // real path), so create it once and reuse the same element afterward.
    let ogUrl = document.getElementById("seo-og-url");
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.id = "seo-og-url";
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute("content", canonicalHref);

    let canonical = document.getElementById("seo-canonical");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.id = "seo-canonical";
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);

    // robots: only ever created for a noindex route, and explicitly removed
    // otherwise — this is a client-side SPA, so navigating away from a
    // noindex page (e.g. NotFound) to a normal one must not leave a stale
    // noindex tag in place for the next route.
    let robots = document.getElementById("seo-robots");
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.id = "seo-robots";
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    // Reset to the shared defaults when this route unmounts, so a route
    // with no SeoHead of its own (nothing left to overwrite these) doesn't
    // keep showing whatever the previous route set.
    return () => {
      if (titleEl) titleEl.textContent = "NameIt: Name, Place, Animal, Thing Online With Friends";
      const defaultDescription =
        "Play the classic Name, Place, Animal, Thing category game free in your browser. Create a private room, invite friends, or play solo against AI.";
      setMetaContent("seo-description", defaultDescription);
      setMetaContent("seo-og-title", "NameIt: Name, Place, Animal, Thing Online With Friends");
      setMetaContent("seo-og-description", defaultDescription);
      setMetaContent("seo-twitter-title", "NameIt: Name, Place, Animal, Thing Online With Friends");
      setMetaContent("seo-twitter-description", defaultDescription);

      // canonical/og:url have no static default in index.html (unlike the
      // text tags above) — they only ever exist because a SeoHead created
      // them. Leaving them in place here would have the next route (if it
      // has no SeoHead of its own) keep advertising THIS route's URL as its
      // canonical/og:url, which is a real, wrong indexing signal. Remove
      // them outright rather than resetting to a value.
      document.getElementById("seo-canonical")?.remove();
      document.getElementById("seo-og-url")?.remove();
    };
  }, [title, description, path, noindex]);

  return null;
}
